import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Cookie, X } from 'lucide-react';

const CookieConsent = () => {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const declineAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true, // Necessary cookies are always required
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:p-6 pointer-events-none">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm pointer-events-auto"
        onClick={() => {}}
      />
      
      {/* Cookie Banner */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl pointer-events-auto animate-slide-up">
        {/* Header */}
        <div className="p-6 pb-4 border-b border-neutral-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center">
                <Cookie className="w-5 h-5 text-neutral-600" />
              </div>
              <h3 className="text-xl font-medium text-neutral-900">
                {t({ fr: 'Politique de Cookies', en: 'Cookie Policy' })}
              </h3>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-neutral-600 leading-relaxed mb-4">
            {t({ 
              fr: 'Nous utilisons des cookies pour améliorer votre expérience sur notre site. Les cookies essentiels sont nécessaires au fonctionnement du site. Vous pouvez choisir d\'accepter ou de refuser les cookies optionnels.',
              en: 'We use cookies to improve your experience on our site. Essential cookies are necessary for the site to function. You can choose to accept or decline optional cookies.'
            })}
          </p>

          {/* Toggle Details */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-neutral-500 hover:text-neutral-700 underline mb-4"
          >
            {showDetails 
              ? t({ fr: 'Masquer les détails', en: 'Hide details' })
              : t({ fr: 'Voir les détails', en: 'View details' })
            }
          </button>

          {/* Cookie Details */}
          {showDetails && (
            <div className="mb-6 space-y-4 p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-neutral-800">
                    {t({ fr: 'Cookies Essentiels', en: 'Essential Cookies' })}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {t({ 
                      fr: 'Nécessaires au fonctionnement du site. Ne peuvent pas être désactivés.',
                      en: 'Required for the site to function. Cannot be disabled.'
                    })}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-neutral-800">
                    {t({ fr: 'Cookies Analytiques', en: 'Analytics Cookies' })}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {t({ 
                      fr: 'Nous aident à comprendre comment vous utilisez le site.',
                      en: 'Help us understand how you use the site.'
                    })}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-neutral-800">
                    {t({ fr: 'Cookies Marketing', en: 'Marketing Cookies' })}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {t({ 
                      fr: 'Utilisés pour personnaliser les publicités.',
                      en: 'Used to personalize advertisements.'
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={acceptAll}
              className="flex-1 px-6 py-3 bg-neutral-900 text-white font-medium rounded-lg hover:bg-neutral-800 transition-colors"
            >
              {t({ fr: 'Accepter tout', en: 'Accept All' })}
            </button>
            <button
              onClick={acceptNecessary}
              className="flex-1 px-6 py-3 bg-neutral-100 text-neutral-700 font-medium rounded-lg hover:bg-neutral-200 transition-colors"
            >
              {t({ fr: 'Essentiels uniquement', en: 'Essential Only' })}
            </button>
            <button
              onClick={declineAll}
              className="px-6 py-3 text-neutral-500 font-medium hover:text-neutral-700 transition-colors"
            >
              {t({ fr: 'Refuser', en: 'Decline' })}
            </button>
          </div>

          {/* Privacy Link */}
          <p className="mt-4 text-xs text-neutral-400 text-center">
            {t({ 
              fr: 'En continuant à naviguer, vous acceptez notre politique de confidentialité.',
              en: 'By continuing to browse, you accept our privacy policy.'
            })}
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CookieConsent;
